import React, { useState, useEffect, useMemo } from 'react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { paymentService } from '../services/paymentService';

const MONTH_NAMES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const formatMonth = (m: string) => { const [y, mo] = m.split('-'); return `${MONTH_NAMES[+mo - 1]}/${y.slice(2)}`; };
const formatCurrency = (v: number) => Math.abs(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface ClassifierInfo { _id: string; name: string; groupId: string; showInPayment: boolean; showInProvisioning: boolean; }
interface GroupInfo { _id: string; name: string; showInPayment?: boolean; showInProvisioning?: boolean; }

const PaymentsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [allMonths, setAllMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [classifiers, setClassifiers] = useState<ClassifierInfo[]>([]);
  const [budgetMap, setBudgetMap] = useState<Record<string, number>>({});
  const [checksMap, setChecksMap] = useState<Record<string, { payment: boolean; provisioning: boolean }>>({});

  const loadData = async () => {
    const res = await paymentService.getEvolution();
    const d = res.data.data;
    setAllMonths(d.allMonths || []);
    setGroups(d.agrupadores || []);
    setClassifiers(d.classificadores || []);

    // Budget map: ID → amount for month
    const bMap: Record<string, number> = {};
    for (const b of (d.budgets || []) as { groupId: string; classifierId: string; month: string; amount: number }[]) {
      const key = b.classifierId ? `${b.classifierId}||${b.month}` : `${b.groupId}||${b.month}`;
      bMap[key] = Number(b.amount) || 0;
    }
    setBudgetMap(bMap);

    // Checks map
    const cMap: Record<string, { payment: boolean; provisioning: boolean }> = {};
    for (const ec of (d.evolutionChecks || []) as { classifierId: string; month: string; payment: boolean; provisioning: boolean }[]) {
      cMap[`${ec.classifierId}||${ec.month}`] = { payment: ec.payment, provisioning: ec.provisioning };
    }
    setChecksMap(cMap);
  };

  useEffect(() => { loadData().finally(() => setLoading(false)); }, []); // eslint-disable-line

  const handleToggle = async (classifierId: string, field: 'payment' | 'provisioning', value: boolean) => {
    const key = `${classifierId}||${selectedMonth}`;
    setChecksMap(prev => ({ ...prev, [key]: { payment: prev[key]?.payment || false, provisioning: prev[key]?.provisioning || false, [field]: value } }));
    await paymentService.toggleCheck(classifierId, selectedMonth, field, value);
  };

  // Filter: only classifiers visible in payment or provisioning
  const visibleItems = useMemo(() => {
    return classifiers.filter(c => c.showInPayment || c.showInProvisioning);
  }, [classifiers]);

  // Group by parent group
  const groupedData = useMemo(() => {
    const groupMap: Record<string, GroupInfo> = {};
    for (const g of groups) groupMap[g._id] = g;

    const map = new Map<string, { group: GroupInfo; items: ClassifierInfo[] }>();
    for (const cls of visibleItems) {
      const grp = groupMap[cls.groupId];
      if (!grp) continue;
      if (!map.has(grp._id)) map.set(grp._id, { group: grp, items: [] });
      map.get(grp._id)!.items.push(cls);
    }
    // Also include groups that have showInPayment/showInProvisioning but no visible classifiers
    for (const grp of groups) {
      if ((grp.showInPayment || grp.showInProvisioning) && !map.has(grp._id)) {
        map.set(grp._id, { group: grp, items: [] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.group.name.localeCompare(b.group.name));
  }, [visibleItems, groups]);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Pagamentos" />
      <div className="px-4 pt-4 pb-2">
        <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm">
          {allMonths.map(m => <option key={m} value={m}>{formatMonth(m)}</option>)}
        </select>
      </div>

      <div className="px-1 py-4 sm:px-4 overflow-x-auto">
        <table className="w-full text-sm border rounded-lg">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Classificação</th>
              <th className="px-3 py-2"></th>
              <th className="px-2 py-2 text-center font-medium text-gray-600 w-10">Pag</th>
              <th className="px-2 py-2 text-center font-medium text-gray-600 w-10">Prov</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 w-28">Orçado</th>
            </tr>
          </thead>
          <tbody>
            {groupedData.map(grp => {
              const grpHasPayment = grp.group.showInPayment === true;
              const grpHasProvisioning = grp.group.showInProvisioning === true;
              const grpKey = `${grp.group._id}||${selectedMonth}`;
              const grpCheck = checksMap[grpKey];
              const grpBudget = budgetMap[`${grp.group._id}||${selectedMonth}`] || 0;
              const visibleCls = grp.items.filter(cls => budgetMap[`${cls._id}||${selectedMonth}`]);
              if (!grpBudget && !visibleCls.length) return null;
              return (
              <React.Fragment key={grp.group._id}>
                {grpBudget > 0 && (
                <tr className="bg-blue-50 border-b">
                  <td className="px-3 py-2 font-semibold text-blue-800">{grp.group.name}</td>
                  <td></td>
                  <td className="px-2 py-2 text-center w-10">
                    {grpHasPayment && <input type="checkbox" checked={grpCheck?.payment || false} onChange={e => handleToggle(grp.group._id, 'payment', e.target.checked)} className="rounded w-3.5 h-3.5" />}
                  </td>
                  <td className="px-2 py-2 text-center w-10">
                    {grpHasProvisioning && <input type="checkbox" checked={grpCheck?.provisioning || false} onChange={e => handleToggle(grp.group._id, 'provisioning', e.target.checked)} className="rounded w-3.5 h-3.5" />}
                  </td>
                  <td className="px-1 py-2 sm:px-3 text-right font-semibold text-blue-800">{formatCurrency(grpBudget)}</td>
                </tr>
                )}
                {!grpBudget && visibleCls.length > 0 && (
                <tr className="bg-blue-50 border-b">
                  <td colSpan={5} className="px-3 py-2 font-semibold text-blue-800">{grp.group.name}</td>
                </tr>
                )}
                {[...visibleCls].sort((a, b) => a.name.localeCompare(b.name)).map(cls => {
                  const key = `${cls._id}||${selectedMonth}`;
                  const check = checksMap[key];
                  const budget = budgetMap[`${cls._id}||${selectedMonth}`] || 0;
                  return (
                    <tr key={cls._id} className="border-b hover:bg-gray-50">
                      <td className="px-1 py-1.5 pl-3 sm:px-3 sm:pl-6 text-gray-700">{cls.name}</td>
                      <td></td>
                      <td className="px-2 py-1.5 text-center w-10">
                        {cls.showInPayment && <input type="checkbox" checked={check?.payment || false} onChange={e => handleToggle(cls._id, 'payment', e.target.checked)} className="rounded w-3.5 h-3.5" />}
                      </td>
                      <td className="px-2 py-1.5 text-center w-10">
                        {cls.showInProvisioning && <input type="checkbox" checked={check?.provisioning || false} onChange={e => handleToggle(cls._id, 'provisioning', e.target.checked)} className="rounded w-3.5 h-3.5" />}
                      </td>
                      <td className="px-1 py-1.5 sm:px-3 text-right text-gray-700">{formatCurrency(budget)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PaymentsPage;
